import { sortBy } from 'lodash';
import { FilterTag } from '@publicisgroupe/p-os-components';

import { ModuleEnum } from './enums';
import {
  FilterDropdownItemProps,
  FiltersResult,
  IdNamePair,
  NavigationItemDto,
} from '../types/types';

export const moduleRouteMappings: Record<string, ModuleEnum> = {
  ['optimizeandreport']: ModuleEnum.Reporting,
  ['pathtogrowth']: ModuleEnum.Growth,
  ['home']: ModuleEnum.Cockpit,
  ['cockpit']: ModuleEnum.Unknown,
};

export const moduleNameToEnum = (moduleNameFromRoute: string): ModuleEnum => {
  moduleNameFromRoute = moduleNameFromRoute.toLowerCase();

  const mappedRoute = moduleRouteMappings[moduleNameFromRoute];
  if (mappedRoute !== undefined) return mappedRoute;

  const routeEnum = Object.keys(ModuleEnum).find(
    (key) => key.toLowerCase() === moduleNameFromRoute
  ) as ModuleEnum;

  return routeEnum ?? ModuleEnum.Unknown;
};

const GLOBAL_FILTERS_ALL = ['-ALL-', 'All'];

export const staticFilters = ['client', 'region', 'country', 'brandcategory', 'brand', 'product'];

export const setSelectedFilterTagValue = (search: string, filter: string) => {
  if (!search && !filter) return '';

  const query = new URLSearchParams(search);
  return query.get(filter);
};

export const readFilterTagsFromQuery = (search: string) => {
  const filterTags: string[] = [];

  if (!search) return filterTags;

  staticFilters.forEach((filter, i) => {
    const filterTagValue = setSelectedFilterTagValue(search, filter);
    if (filterTagValue) filterTags[i] = filterTagValue;
  });
  return filterTags;
};

export const addFiltersToRoute = (
  currentSearchQuery: string | undefined,
  targetSearchQuery: string
): string | undefined => {
  // if target location.search doesn't have filters but last page call had, add filters to location.search
  if (!currentSearchQuery || readFilterTagsFromQuery(targetSearchQuery).length > 0) {
    return;
  }

  const targetParams = new URLSearchParams(targetSearchQuery);
  const resultParams = new URLSearchParams(currentSearchQuery);

  targetParams.forEach((value, key) => {
    resultParams.get(key);
    if (resultParams.get(key)) {
      resultParams.set(key, value);
    } else {
      resultParams.append(key, value);
    }
  });

  if (
    currentSearchQuery.toString() !== resultParams.toString() ||
    targetParams.toString() !== resultParams.toString()
  ) {
    return resultParams.toString();
  }
};

export const constructSearchQueryFromFilters = (filters: string[]): string => {
  const newUrl = new URL(window.location.origin);

  filters.forEach((x, i) => {
    if (i < staticFilters.length && x !== '') {
      newUrl.searchParams.append(staticFilters[i], x);
    }
  });

  if (filters.length > 0) {
    localStorage.setItem('initial-client', filters[0]);
  }

  return newUrl.searchParams.toString();
};

const validateFiltersOnEachLevel = (
  currLevel: number,
  startLevel: number,
  tempFilters: any,
  startFilters: any,
  applyPrevFilters: boolean,
  result: any[],
  data: IdNamePair[]
) => {
  if (currLevel > startLevel && tempFilters.length > 0) {
    // checks if previously selected filter of this level was the same. Example: different client but same country & region
    const previouslySelectedFilter = tempFilters.find(
      (filter: any) => filter.name === startFilters[currLevel]
    );
    if (!previouslySelectedFilter) {
      applyPrevFilters = false;
    }
    result[currLevel] = applyPrevFilters ? previouslySelectedFilter?.name : tempFilters[0].name;
    data[currLevel] = applyPrevFilters ? previouslySelectedFilter : tempFilters[0];
  }
  return {
    response: result,
    prevFilters: applyPrevFilters,
    data: data,
  };
};

const getInitialFilterValue = (tempFilters: NavigationItemDto[], initialClient?: string) => {
  if (tempFilters.length > 0 && tempFilters[0].name) {
    return [initialClient ? initialClient : tempFilters[0].name];
  }
  return [];
};

export const getDefaultFilters = (
  filters: NavigationItemDto[],
  filterNames: string[],
  startFilters: any[] = [],
  startLevel = 0
) => {
  if (filters.length == 0 || filterNames.length == 0) {
    return { selectedOptions: [], data: [] } as FiltersResult;
  }

  let currLevel = 0;
  let tempFilters = filters;

  const initialClient = localStorage.getItem('initial-client');
  let result: any[] = getInitialFilterValue(tempFilters, initialClient ?? '');

  const foundFilter = filters.find((x) => x.name === result[0]);
  const selectedFilters: IdNamePair[] = [
    { id: foundFilter?.id ?? '', name: foundFilter?.name ?? '' },
  ];

  if (startFilters.length > 0) {
    result = [...startFilters];
  }
  let applyPrevFilters = true;

  //goes through nested filters, sets first filter for each level
  while (currLevel < filterNames.length) {
    const { response, prevFilters, data } = validateFiltersOnEachLevel(
      currLevel,
      startLevel,
      tempFilters,
      startFilters,
      applyPrevFilters,
      result,
      selectedFilters
    );

    result = response;
    applyPrevFilters = prevFilters;
    selectedFilters.push(...data);

    const selectedFilterName = result[currLevel] ?? '';
    const filtersFound = tempFilters.find((x) => x.name === selectedFilterName);
    if (currLevel + 1 == filterNames.length) {
      break;
    }

    tempFilters = getFilterFoundItems(filtersFound);
    currLevel++;
  }

  return { selectedOptions: result, data: selectedFilters } as FiltersResult;
};

// loops down the arr hierarchy for x levels and returns the data array for that level
const getFilterLevel = (level: number, filters: NavigationItemDto[], selectedFilters: string[]) => {
  let currLevel = 0;
  let tempFilters = filters;

  while (currLevel < level) {
    const selectedFilterName = selectedFilters[currLevel] ?? '';
    const filtersFound = tempFilters.find((x) => x.name === selectedFilterName);

    tempFilters = getFilterFoundItems(filtersFound);

    currLevel++;
  }

  return tempFilters;
};

// returns possible drowpdown options for a level
export const getOptionsForLevel = (
  level: number,
  filters: NavigationItemDto[],
  filterNames: string[],
  selectedFilters: string[],
  isForDropdown: boolean
) => {
  const filtersForLevel = getFilterLevel(level, filters, selectedFilters);
  let options: FilterDropdownItemProps[] = [];
  if (isForDropdown) {
    const infoOption: FilterDropdownItemProps = {
      key: '___',
      text: '',
      disabled: true,
      id: '',
    };

    if (filtersForLevel.length === 0) {
      infoOption.text = level > 0 ? `Select a ${filterNames[level - 1]} first` : '';
      infoOption.className = 'empty';
    } else {
      infoOption.text = filterNames[level];
    }

    options = [infoOption];
  }

  for (let index = 0; index < filtersForLevel.length; index++) {
    const element = filtersForLevel[index];
    options.push({
      key: index,
      id: element.id,
      value: element.name,
      text: element.name,
    } as FilterDropdownItemProps);
  }

  return options;
};

export const setCookie = (name: string, value: any, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + 24 * 60 * 60 * 1000 * days);
  document.cookie = name + '=' + value + ';path=/;expires=' + date.toUTCString();
};

export const getCookie = (name: string) => {
  const cookie = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
  return cookie ? cookie[2] : null;
};

export const deleteCookie = (name: string) => {
  setCookie(name, '', -1);
};

const sortSubFilters = (data: NavigationItemDto, property: string): NavigationItemDto[] => {
  const sortedArray = sortBy(data.items, property);

  // Move -ALL- to the end of the array
  const indexOfAll = sortedArray.findIndex((item) => GLOBAL_FILTERS_ALL.includes(item.name));
  if (indexOfAll !== -1) {
    sortedArray.push(sortedArray.splice(indexOfAll, 1)[0]);
  }

  sortedArray.forEach((item) => {
    if (item?.items?.length > 0) {
      item.items = sortSubFilters(item, property);
    }
  });

  return sortedArray;
};

export const getSortedFiltersData = (data: NavigationItemDto[], property: string) => {
  const sortedClientsArray = sortBy(data, property);

  const filtersSortedArray: NavigationItemDto[] = sortedClientsArray.map((item) => ({
    ...item,
    items: sortSubFilters(item, property),
  }));

  return filtersSortedArray;
};

export const getHostByModule = (settings: any, moduleName: string) => {
  return settings?.hosts?.find((x: any) => x.module === moduleName)?.host ?? '';
};
export const getQueryParamValue = (filters: FilterTag[], filterType: string) => {
  const result = filters.filter((f) => f.key === filterType)[0];
  return result ? result.value : '';
};

export const getFilterFoundItems = (
  filtersFound: NavigationItemDto | undefined
): NavigationItemDto[] => {
  if (filtersFound && filtersFound.items) return filtersFound.items;
  return [];
};

export const getSelectedFilters = (
  filters: NavigationItemDto[],
  filterTags: string[]
): IdNamePair[] => {
  const client = filters.find((x) => x.name === filterTags[0]);
  const region = client?.items.find((x) => x.name === filterTags[1]);
  const country = region?.items.find((x) => x.name === filterTags[2]);
  const brandCategory = country?.items.find((x) => x.name === filterTags[3]);
  const brand = brandCategory?.items.find((x) => x.name === filterTags[4]);
  const product = brand?.items.find((x) => x.name === filterTags[5]);

  const selectedFilters: IdNamePair[] = [
    { id: client?.id ?? null, name: client?.name ?? '' },
    { id: region?.id ?? null, name: region?.name ?? '' },
    { id: country?.id ?? null, name: country?.name ?? '' },
    { id: brandCategory?.id ?? '', name: brandCategory?.name ?? '' },
    { id: brand?.id ?? '', name: brand?.name ?? '' },
    { id: product?.id ?? '', name: product?.name ?? '' },
  ];

  return selectedFilters;
};

export const getEmailDomain = (userEmail: string) => {
  if (!userEmail) {
    return '';
  }
  const splitEmail = userEmail.split('@');
  return `@${splitEmail[splitEmail.length - 1]}`;
};
